import { useEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

/**
 * Start every new page at the top, and put people back where they were when
 * they press Back.
 *
 * With client-side routing the address changes without the page reloading, so
 * the browser has no reason to move the scroll position — it simply stays
 * where it was. Scroll halfway down the project list, open a project, and the
 * project opens halfway down. Every section had this; the project list was
 * just the longest page and so the most obvious.
 *
 * Going forward scrolls to the top. Going back restores the position that page
 * was left at, which is the one case where staying put is what somebody wants:
 * returning to a list should return them to the row they came from, not to its
 * first row.
 */
export default function ScrollToTop() {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType();
  const positions = useRef(new Map());
  const previous = useRef(pathname);

  // Record where each page was left, on the way out rather than on the way in.
  useEffect(() => {
    const from = previous.current;
    return () => {
      positions.current.set(from, window.scrollY);
    };
  }, [pathname]);

  useEffect(() => {
    previous.current = pathname;

    // A link to an anchor means the target is the point, not the top.
    if (hash) return;

    if (navigationType === "POP") {
      const saved = positions.current.get(pathname);
      if (typeof saved === "number") {
        window.scrollTo({ top: saved, behavior: "instant" });
        return;
      }
    }

    // "instant" rather than smooth: a page that scrolls itself into place over
    // half a second feels like it is still loading.
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname, hash, navigationType]);

  return null;
}
